import {
  publishGovernorRuntimeEvidence,
} from '../runtime-evidence/GovernorRuntimeEvidenceBus';

import {
  emitTreeGovernorEvent,
} from '../../lib/graph/graphMutationAudit';

export type CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind =
  | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FALLBACK_ACTIVATED'
  | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE';

export type CanonicalTreeHistoricalAuthorityPresentationLifecycleReason =
  | 'NONE'
  | 'PROJECTION_OR_FEED_FAILURE'
  | 'NULL_OR_UNDEFINED_PROJECTION'
  | 'MALFORMED_PROJECTION'
  | 'CARD_RENDER_FAILURE';

export type CanonicalTreeHistoricalAuthorityPresentationEpisodeStatus =
  | 'OPEN'
  | 'CLOSED';

export type CanonicalTreeHistoricalAuthorityPresentationResolutionReason =
  | 'AVAILABLE_PROJECTION'
  | 'MIGRATED_HISTORICAL_RECORD'
  | null;

export interface CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord {
  readonly recordId:
    string;

  readonly schemaVersion:
    2;

  readonly milestone:
    'CC-TREE-ARCH-082';

  readonly sourceObservabilityMilestone:
    'CC-TREE-ARCH-079';

  readonly sourceContainmentMilestone:
    'CC-TREE-ARCH-076';

  readonly eventKind:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind;

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleReason;

  readonly correlationId:
    string;

  readonly episodeId:
    string;

  readonly episodeStatus:
    CanonicalTreeHistoricalAuthorityPresentationEpisodeStatus;

  readonly occurrenceCount:
    number;

  readonly occurredAt:
    string;

  readonly resolvedAt:
    string | null;

  readonly resolutionReason:
    CanonicalTreeHistoricalAuthorityPresentationResolutionReason;

  readonly safeFailureMessage:
    string | null;

  readonly classification:
    'TREE_GOVERNOR_INTERNAL_ONLY';

  readonly containsProjectionPayload:
    false;

  readonly containsFamilyMemberData:
    false;

  readonly containsRelationshipData:
    false;

  readonly containsAuthenticationData:
    false;

  readonly containsMembershipData:
    false;

  readonly containsStackTrace:
    false;

  readonly affectsRuntimeDecision:
    false;

  readonly affectsRuntimeGuard:
    false;

  readonly affectsOperationalCertification:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly memberFacing:
    false;
}

export interface CanonicalTreeHistoricalAuthorityPresentationActiveEpisode {
  readonly correlationId:
    string;

  readonly episodeId:
    string;

  readonly eventKind:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind;

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleReason;

  readonly openedAt:
    string;

  readonly lastOccurrenceAt:
    string;

  readonly occurrenceCount:
    number;
}

export interface CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata {
  readonly detectedAt:
    string;

  readonly reason:
    'INVALID_JSON'
    | 'INVALID_ENVELOPE'
    | 'UNSUPPORTED_SCHEMA'
    | 'STORAGE_UNAVAILABLE'
    | 'STORAGE_QUOTA_EXCEEDED';

  readonly sourceKey:
    string;

  readonly rawPayloadPersisted:
    false;
}

export interface CanonicalTreeHistoricalAuthorityPresentationMigrationMetadata {
  readonly migrated:
    boolean;

  readonly sourceSchemaVersion:
    1 | null;

  readonly targetSchemaVersion:
    2;

  readonly migratedAt:
    string | null;

  readonly migratedRecordCount:
    number;

  readonly legacyKeyRemoved:
    boolean;
}

export interface CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope {
  readonly schemaVersion:
    2;

  readonly updatedAt:
    string;

  readonly records:
    readonly CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord[];

  readonly activeEpisodes:
    readonly CanonicalTreeHistoricalAuthorityPresentationActiveEpisode[];

  readonly quarantine:
    CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata | null;

  readonly migration:
    CanonicalTreeHistoricalAuthorityPresentationMigrationMetadata;
}

export interface CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot {
  readonly generatedAt:
    string;

  readonly schemaVersion:
    2;

  readonly totalRecords:
    number;

  readonly openEpisodeCount:
    number;

  readonly records:
    readonly CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord[];

  readonly activeEpisodes:
    readonly CanonicalTreeHistoricalAuthorityPresentationActiveEpisode[];

  readonly quarantine:
    CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata | null;

  readonly migration:
    CanonicalTreeHistoricalAuthorityPresentationMigrationMetadata;
}

export interface CanonicalTreeHistoricalAuthorityPresentationLifecycleImplementation {
  readonly milestone:
    'CC-TREE-ARCH-082';

  readonly authority:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_IMPLEMENTATION';

  readonly decision:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_IMPLEMENTED';

  readonly auditMilestone:
    'CC-TREE-ARCH-081';

  readonly schemaVersion:
    2;

  readonly legacySchemaVersion:
    1;

  readonly boundedHistoryCapacity:
    64;

  readonly deterministicMigrationImplemented:
    true;

  readonly deterministicRecordIdsImplemented:
    true;

  readonly deterministicEpisodeIdsImplemented:
    true;

  readonly episodeScopedOccurrenceCountingImplemented:
    true;

  readonly availableProjectionResolutionImplemented:
    true;

  readonly closedRecordsRetained:
    true;

  readonly crossTabSynchronizationImplemented:
    true;

  readonly crossTabEvidenceRepublishingPrevented:
    true;

  readonly corruptionQuarantineImplemented:
    true;

  readonly rawCorruptPayloadPersistenceAuthorized:
    false;

  readonly internalResetRestricted:
    true;

  readonly manualCommandCenterClearAuthorized:
    false;

  readonly canonicalGovernorRuntimeDispatchInvoked:
    false;

  readonly runtimeDecisionDependencyAdded:
    false;

  readonly runtimeGuardDependencyAdded:
    false;

  readonly operationalCertificationDependencyAdded:
    false;

  readonly certifiesTreeHealth:
    false;

  readonly certifiesReleaseReadiness:
    false;

  readonly certifiesDeploymentReadiness:
    false;

  readonly memberFacingStateAdded:
    false;

  readonly lifecycleCoreImplemented:
    true;

  readonly productionWiringCompleted:
    true;

  readonly nextGovernedDependency:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_CERTIFICATION';
}

interface LegacyV1Record {
  readonly milestone?:
    unknown;

  readonly eventKind?:
    unknown;

  readonly reason?:
    unknown;

  readonly sourceMilestone?:
    unknown;

  readonly containmentStatus?:
    unknown;

  readonly correlationId?:
    unknown;

  readonly occurrenceCount?:
    unknown;

  readonly occurredAt?:
    unknown;

  readonly safeFailureMessage?:
    unknown;

  readonly classification?:
    unknown;
}

export interface CanonicalTreeHistoricalAuthorityPresentationLifecycleFailureInput {
  readonly eventKind:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind;

  readonly reason:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleReason;

  readonly correlationId:
    string;

  readonly occurredAt:
    string;

  readonly safeFailureMessage:
    string | null;
}

const LEGACY_STORAGE_KEY =
  'kc:tree-historical-authority-presentation-observability:v1';

const STORAGE_KEY =
  'kc:tree-historical-authority-presentation-observability:v2';

const HISTORY_CAPACITY =
  64;

let memoryEnvelope:
CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope | null =
  null;

let loaded =
  false;

const isRecord = (
  value:
    unknown,
): value is Record<string, unknown> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value);

const validDate = (
  value:
    unknown,
): value is string =>
  typeof value === 'string' &&
  !Number.isNaN(
    Date.parse(value),
  );

const stableHash = (
  value:
    string,
): string => {
  let hash =
    2166136261;

  for (
    let index = 0;
    index < value.length;
    index += 1
  ) {
    hash ^=
      value.charCodeAt(index);

    hash =
      Math.imul(
        hash,
        16777619,
      );
  }

  return (
    hash >>> 0
  )
    .toString(16)
    .padStart(8, '0')
    .toUpperCase();
};

const freezeArray = <T>(
  values:
    readonly T[],
): readonly T[] =>
  Object.freeze([
    ...values,
  ]);

const createEmptyMigration = ():
CanonicalTreeHistoricalAuthorityPresentationMigrationMetadata =>
  Object.freeze({
    migrated:
      false,

    sourceSchemaVersion:
      null,

    targetSchemaVersion:
      2,

    migratedAt:
      null,

    migratedRecordCount:
      0,

    legacyKeyRemoved:
      false,
  });

const createEmptyEnvelope = (
  quarantine:
    CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata | null =
      null,
): CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope =>
  Object.freeze({
    schemaVersion:
      2,

    updatedAt:
      new Date().toISOString(),

    records:
      freezeArray([]),

    activeEpisodes:
      freezeArray([]),

    quarantine,

    migration:
      createEmptyMigration(),
  });

const isEventKind = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind =>
  value ===
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_FALLBACK_ACTIVATED' ||
  value ===
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_RENDER_FAILURE';

const isReason = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationLifecycleReason =>
  value === 'NONE' ||
  value === 'PROJECTION_OR_FEED_FAILURE' ||
  value === 'NULL_OR_UNDEFINED_PROJECTION' ||
  value === 'MALFORMED_PROJECTION' ||
  value === 'CARD_RENDER_FAILURE';

const isLifecycleRecord = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    value.schemaVersion ===
      2 &&
    value.milestone ===
      'CC-TREE-ARCH-082' &&
    value.sourceObservabilityMilestone ===
      'CC-TREE-ARCH-079' &&
    value.sourceContainmentMilestone ===
      'CC-TREE-ARCH-076' &&
    typeof value.recordId ===
      'string' &&
    value.recordId.length >
      0 &&
    isEventKind(
      value.eventKind,
    ) &&
    isReason(
      value.reason,
    ) &&
    typeof value.correlationId ===
      'string' &&
    typeof value.episodeId ===
      'string' &&
    (
      value.episodeStatus ===
        'OPEN' ||
      value.episodeStatus ===
        'CLOSED'
    ) &&
    typeof value.occurrenceCount ===
      'number' &&
    Number.isFinite(
      value.occurrenceCount,
    ) &&
    value.occurrenceCount >=
      1 &&
    validDate(
      value.occurredAt,
    ) &&
    (
      value.resolvedAt ===
        null ||
      validDate(
        value.resolvedAt,
      )
    ) &&
    (
      value.resolutionReason ===
        null ||
      value.resolutionReason ===
        'AVAILABLE_PROJECTION' ||
      value.resolutionReason ===
        'MIGRATED_HISTORICAL_RECORD'
    ) &&
    (
      value.safeFailureMessage ===
        null ||
      typeof value.safeFailureMessage ===
        'string'
    ) &&
    value.classification ===
      'TREE_GOVERNOR_INTERNAL_ONLY' &&
    value.containsProjectionPayload ===
      false &&
    value.containsFamilyMemberData ===
      false &&
    value.containsRelationshipData ===
      false &&
    value.containsAuthenticationData ===
      false &&
    value.containsMembershipData ===
      false &&
    value.containsStackTrace ===
      false &&
    value.affectsRuntimeDecision ===
      false &&
    value.affectsRuntimeGuard ===
      false &&
    value.affectsOperationalCertification ===
      false &&
    value.certifiesTreeHealth ===
      false &&
    value.certifiesReleaseReadiness ===
      false &&
    value.certifiesDeploymentReadiness ===
      false &&
    value.memberFacing ===
      false
  );
};

const isActiveEpisode = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationActiveEpisode => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.correlationId ===
      'string' &&
    typeof value.episodeId ===
      'string' &&
    isEventKind(
      value.eventKind,
    ) &&
    isReason(
      value.reason,
    ) &&
    validDate(
      value.openedAt,
    ) &&
    validDate(
      value.lastOccurrenceAt,
    ) &&
    typeof value.occurrenceCount ===
      'number' &&
    Number.isFinite(
      value.occurrenceCount,
    ) &&
    value.occurrenceCount >=
      1
  );
};

const isMigrationMetadata = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationMigrationMetadata => {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.migrated ===
      'boolean' &&
    (
      value.sourceSchemaVersion ===
        null ||
      value.sourceSchemaVersion ===
        1
    ) &&
    value.targetSchemaVersion ===
      2 &&
    (
      value.migratedAt ===
        null ||
      validDate(
        value.migratedAt,
      )
    ) &&
    typeof value.migratedRecordCount ===
      'number' &&
    Number.isFinite(
      value.migratedRecordCount,
    ) &&
    value.migratedRecordCount >=
      0 &&
    typeof value.legacyKeyRemoved ===
      'boolean'
  );
};

const isQuarantineMetadata = (
  value:
    unknown,
): value is CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata => {
  if (value === null) {
    return true;
  }

  if (!isRecord(value)) {
    return false;
  }

  return (
    validDate(
      value.detectedAt,
    ) &&
    (
      value.reason ===
        'INVALID_JSON' ||
      value.reason ===
        'INVALID_ENVELOPE' ||
      value.reason ===
        'UNSUPPORTED_SCHEMA' ||
      value.reason ===
        'STORAGE_UNAVAILABLE' ||
      value.reason ===
        'STORAGE_QUOTA_EXCEEDED'
    ) &&
    typeof value.sourceKey ===
      'string' &&
    value.rawPayloadPersisted ===
      false
  );
};

const parseEnvelope = (
  value:
    unknown,
): CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (
    value.schemaVersion !==
    2 ||
    !validDate(
      value.updatedAt,
    ) ||
    !Array.isArray(
      value.records,
    ) ||
    !Array.isArray(
      value.activeEpisodes,
    ) ||
    !isQuarantineMetadata(
      value.quarantine,
    ) ||
    !isMigrationMetadata(
      value.migration,
    )
  ) {
    return null;
  }

  const records =
    value.records
      .filter(
        isLifecycleRecord,
      )
      .slice(
        0,
        HISTORY_CAPACITY,
      );

  const recordIds =
    new Set<string>();

  const deduplicatedRecords =
    records.filter(
      record => {
        if (
          recordIds.has(
            record.recordId,
          )
        ) {
          return false;
        }

        recordIds.add(
          record.recordId,
        );

        return true;
      },
    );

  const episodesById =
    new Map<string, CanonicalTreeHistoricalAuthorityPresentationActiveEpisode>();

  for (
    const episode of value.activeEpisodes
  ) {
    if (
      isActiveEpisode(
        episode,
      )
    ) {
      episodesById.set(
        episode.episodeId,
        episode,
      );
    }
  }

  return Object.freeze({
    schemaVersion:
      2,

    updatedAt:
      value.updatedAt,

    records:
      freezeArray(
        deduplicatedRecords,
      ),

    activeEpisodes:
      freezeArray(
        [
          ...episodesById.values(),
        ],
      ),

    quarantine:
      value.quarantine,

    migration:
      value.migration,
  });
};

const publishLifecycleEvidence = (
  kind:
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_MIGRATED'
    | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_CORRUPTION_RECOVERED'
    | 'TREE_HISTORICAL_AUTHORITY_PRESENTATION_EPISODE_CLOSED',

  detail:
    Readonly<Record<string, unknown>>,
): void => {
  try {
    emitTreeGovernorEvent(
      kind,
      {
        milestone:
          'CC-TREE-ARCH-082',

        classification:
          'TREE_GOVERNOR_INTERNAL_ONLY',

        containsPrivateFamilyData:
          false,

        affectsRuntimeAuthority:
          false,

        certifiesTreeHealth:
          false,

        certifiesReleaseReadiness:
          false,

        certifiesDeploymentReadiness:
          false,

        ...detail,
      },
    );
  } catch {
    // Lifecycle evidence must never escape into presentation rendering.
  }

  try {
    publishGovernorRuntimeEvidence({
      domain:
        'tree',

      source:
        'CC-TREE-ARCH-082',

      severity:
        kind ===
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_CORRUPTION_RECOVERED'
          ? 'warn'
          : 'info',

      summary:
        kind,

      evidence:
        [
          'classification=TREE_GOVERNOR_INTERNAL_ONLY',
          'containsPrivateFamilyData=false',
          'affectsRuntimeAuthority=false',
        ],

      owner:
        'tree-governor',

      repairReady:
        false,
    });
  } catch {
    // Shared runtime evidence remains best effort and secondary.
  }
};

type CanonicalTreeHistoricalAuthorityPresentationLifecycleSubscriber =
  (
    snapshot:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot,
  ) => void;

const lifecycleSubscribers =
  new Set<
    CanonicalTreeHistoricalAuthorityPresentationLifecycleSubscriber
  >();

const notifyLifecycleSubscribers =
  (): void => {
    const snapshot =
      readCanonicalTreeHistoricalAuthorityPresentationLifecycle();

    for (const subscriber of [
      ...lifecycleSubscribers,
    ]) {
      try {
        subscriber(
          snapshot,
        );
      } catch {
        // Subscriber failure must not alter canonical lifecycle state.
      }
    }
  };

const persistEnvelope = (
  envelope:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope,
): boolean => {
  if (
    typeof window ===
    'undefined'
  ) {
    memoryEnvelope =
      envelope;

    notifyLifecycleSubscribers();

    return true;
  }

  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify(
        envelope,
      ),
    );

    memoryEnvelope =
      envelope;

    notifyLifecycleSubscribers();

    return true;
  } catch (error) {
    const reason =
      error instanceof DOMException &&
      (
        error.name ===
          'QuotaExceededError' ||
        error.name ===
          'NS_ERROR_DOM_QUOTA_REACHED'
      )
        ? 'STORAGE_QUOTA_EXCEEDED'
        : 'STORAGE_UNAVAILABLE';

    memoryEnvelope =
      createEmptyEnvelope(
        Object.freeze({
          detectedAt:
            new Date().toISOString(),

          reason,

          sourceKey:
            STORAGE_KEY,

          rawPayloadPersisted:
            false,
        }),
      );

    notifyLifecycleSubscribers();

    publishLifecycleEvidence(
      'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_CORRUPTION_RECOVERED',
      {
        reason,
      },
    );

    return false;
  }
};

const createRecordId = (
  input:
    Readonly<{
      eventKind:
        CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind;

      reason:
        CanonicalTreeHistoricalAuthorityPresentationLifecycleReason;

      correlationId:
        string;

      episodeId:
        string;

      occurrenceCount:
        number;

      occurredAt:
        string;
    }>,
): string =>
  `TREE-HIST-REC-${stableHash(
    [
      input.eventKind,
      input.reason,
      input.correlationId,
      input.episodeId,
      String(
        input.occurrenceCount,
      ),
      input.occurredAt,
    ].join('|'),
  )}`;

const createEpisodeId = (
  input:
    Readonly<{
      eventKind:
        CanonicalTreeHistoricalAuthorityPresentationLifecycleEventKind;

      reason:
        CanonicalTreeHistoricalAuthorityPresentationLifecycleReason;

      correlationId:
        string;

      openedAt:
        string;
    }>,
): string =>
  `TREE-HIST-EP-${stableHash(
    [
      input.eventKind,
      input.reason,
      input.correlationId,
      input.openedAt,
    ].join('|'),
  )}`;

const migrateLegacyRecord = (
  value:
    LegacyV1Record,

  index:
    number,
): CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord | null => {
  if (
    !isEventKind(
      value.eventKind,
    ) ||
    !isReason(
      value.reason,
    ) ||
    typeof value.correlationId !==
      'string' ||
    typeof value.occurrenceCount !==
      'number' ||
    !Number.isFinite(
      value.occurrenceCount,
    ) ||
    value.occurrenceCount <
      1 ||
    !validDate(
      value.occurredAt,
    )
  ) {
    return null;
  }

  const episodeId =
    `TREE-HIST-MIGRATED-EP-${stableHash(
      [
        value.correlationId,
        value.occurredAt,
        String(index),
      ].join('|'),
    )}`;

  return Object.freeze({
    recordId:
      `TREE-HIST-MIGRATED-REC-${stableHash(
        [
          value.correlationId,
          value.occurredAt,
          String(
            value.occurrenceCount,
          ),
          String(index),
        ].join('|'),
      )}`,

    schemaVersion:
      2,

    milestone:
      'CC-TREE-ARCH-082',

    sourceObservabilityMilestone:
      'CC-TREE-ARCH-079',

    sourceContainmentMilestone:
      'CC-TREE-ARCH-076',

    eventKind:
      value.eventKind,

    reason:
      value.reason,

    correlationId:
      value.correlationId,

    episodeId,

    episodeStatus:
      'CLOSED',

    occurrenceCount:
      value.occurrenceCount,

    occurredAt:
      value.occurredAt,

    resolvedAt:
      value.occurredAt,

    resolutionReason:
      'MIGRATED_HISTORICAL_RECORD',

    safeFailureMessage:
      typeof value.safeFailureMessage ===
      'string'
        ? value.safeFailureMessage
        : null,

    classification:
      'TREE_GOVERNOR_INTERNAL_ONLY',

    containsProjectionPayload:
      false,

    containsFamilyMemberData:
      false,

    containsRelationshipData:
      false,

    containsAuthenticationData:
      false,

    containsMembershipData:
      false,

    containsStackTrace:
      false,

    affectsRuntimeDecision:
      false,

    affectsRuntimeGuard:
      false,

    affectsOperationalCertification:
      false,

    certifiesTreeHealth:
      false,

    certifiesReleaseReadiness:
      false,

    certifiesDeploymentReadiness:
      false,

    memberFacing:
      false,
  });
};

const migrateLegacyV1 = ():
CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope | null => {
  if (
    typeof window ===
    'undefined'
  ) {
    return null;
  }

  try {
    const raw =
      window.localStorage.getItem(
        LEGACY_STORAGE_KEY,
      );

    if (!raw) {
      return null;
    }

    const parsed =
      JSON.parse(raw);

    if (!Array.isArray(parsed)) {
      return null;
    }

    const migratedAt =
      new Date().toISOString();

    const records =
      parsed
        .map(
          (
            value,
            index,
          ) =>
            migrateLegacyRecord(
              value as LegacyV1Record,
              index,
            ),
        )
        .filter(
          (
            value,
          ): value is CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord =>
            value !==
            null,
        )
        .slice(
          0,
          HISTORY_CAPACITY,
        );

    const envelope:
    CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope =
      Object.freeze({
        schemaVersion:
          2,

        updatedAt:
          migratedAt,

        records:
          freezeArray(
            records,
          ),

        activeEpisodes:
          freezeArray([]),

        quarantine:
          null,

        migration:
          Object.freeze({
            migrated:
              true,

            sourceSchemaVersion:
              1,

            targetSchemaVersion:
              2,

            migratedAt,

            migratedRecordCount:
              records.length,

            legacyKeyRemoved:
              false,
          }),
      });

    if (
      !persistEnvelope(
        envelope,
      )
    ) {
      return memoryEnvelope;
    }

    let legacyKeyRemoved =
      false;

    try {
      window.localStorage.removeItem(
        LEGACY_STORAGE_KEY,
      );

      legacyKeyRemoved =
        true;
    } catch {
      legacyKeyRemoved =
        false;
    }

    const finalized =
      Object.freeze({
        ...envelope,

        updatedAt:
          new Date().toISOString(),

        migration:
          Object.freeze({
            ...envelope.migration,

            legacyKeyRemoved,
          }),
      });

    persistEnvelope(
      finalized,
    );

    publishLifecycleEvidence(
      'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_MIGRATED',
      {
        migratedRecordCount:
          records.length,

        legacyKeyRemoved,
      },
    );

    return finalized;
  } catch {
    return null;
  }
};

const recoverCorruptStore = (
  reason:
    CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata['reason'],
): CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope => {
  const recovered =
    createEmptyEnvelope(
      Object.freeze({
        detectedAt:
          new Date().toISOString(),

        reason,

        sourceKey:
          STORAGE_KEY,

        rawPayloadPersisted:
          false,
      }),
    );

  persistEnvelope(
    recovered,
  );

  publishLifecycleEvidence(
    'TREE_HISTORICAL_AUTHORITY_PRESENTATION_LIFECYCLE_CORRUPTION_RECOVERED',
    {
      reason,
    },
  );

  return recovered;
};

const loadEnvelope = (
  forceReload =
    false,
): CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope => {
  if (
    loaded &&
    !forceReload &&
    memoryEnvelope
  ) {
    return memoryEnvelope;
  }

  loaded =
    true;

  if (
    typeof window ===
    'undefined'
  ) {
    memoryEnvelope ??=
      createEmptyEnvelope();

    return memoryEnvelope;
  }

  try {
    const raw =
      window.localStorage.getItem(
        STORAGE_KEY,
      );

    if (!raw) {
      const migrated =
        migrateLegacyV1();

      memoryEnvelope =
        migrated ??
        createEmptyEnvelope();

      if (!migrated) {
        persistEnvelope(
          memoryEnvelope,
        );
      }

      return memoryEnvelope;
    }

    let parsed:
    unknown;

    try {
      parsed =
        JSON.parse(raw);
    } catch {
      return recoverCorruptStore(
        'INVALID_JSON',
      );
    }

    if (
      isRecord(parsed) &&
      parsed.schemaVersion !==
        2
    ) {
      return recoverCorruptStore(
        'UNSUPPORTED_SCHEMA',
      );
    }

    const envelope =
      parseEnvelope(
        parsed,
      );

    if (!envelope) {
      return recoverCorruptStore(
        'INVALID_ENVELOPE',
      );
    }

    memoryEnvelope =
      envelope;

    return envelope;
  } catch {
    return recoverCorruptStore(
      'STORAGE_UNAVAILABLE',
    );
  }
};

const updateEnvelope = (
  input:
    Readonly<{
      records:
        readonly CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord[];

      activeEpisodes:
        readonly CanonicalTreeHistoricalAuthorityPresentationActiveEpisode[];

      quarantine?:
        CanonicalTreeHistoricalAuthorityPresentationQuarantineMetadata | null;
    }>,
): CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope => {
  const current =
    loadEnvelope();

  const envelope:
  CanonicalTreeHistoricalAuthorityPresentationLifecycleEnvelope =
    Object.freeze({
      schemaVersion:
        2,

      updatedAt:
        new Date().toISOString(),

      records:
        freezeArray(
          input.records.slice(
            0,
            HISTORY_CAPACITY,
          ),
        ),

      activeEpisodes:
        freezeArray(
          input.activeEpisodes,
        ),

      quarantine:
        input.quarantine ===
        undefined
          ? current.quarantine
          : input.quarantine,

      migration:
        current.migration,
    });

  persistEnvelope(
    envelope,
  );

  return envelope;
};

export const recordCanonicalTreeHistoricalAuthorityPresentationLifecycleFailure =
  (
    input:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleFailureInput,
  ): CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord | null => {
    try {
      const current =
        loadEnvelope();

      const activeEpisode =
        current.activeEpisodes.find(
          episode =>
            episode.correlationId ===
            input.correlationId &&
            episode.eventKind ===
            input.eventKind &&
            episode.reason ===
            input.reason,
        );

      const episodeId =
        activeEpisode?.episodeId ??
        createEpisodeId({
          eventKind:
            input.eventKind,

          reason:
            input.reason,

          correlationId:
            input.correlationId,

          openedAt:
            input.occurredAt,
        });

      const occurrenceCount =
        (
          activeEpisode?.occurrenceCount ??
          0
        ) + 1;

      const record:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleRecord =
        Object.freeze({
          recordId:
            createRecordId({
              eventKind:
                input.eventKind,

              reason:
                input.reason,

              correlationId:
                input.correlationId,

              episodeId,

              occurrenceCount,

              occurredAt:
                input.occurredAt,
            }),

          schemaVersion:
            2,

          milestone:
            'CC-TREE-ARCH-082',

          sourceObservabilityMilestone:
            'CC-TREE-ARCH-079',

          sourceContainmentMilestone:
            'CC-TREE-ARCH-076',

          eventKind:
            input.eventKind,

          reason:
            input.reason,

          correlationId:
            input.correlationId,

          episodeId,

          episodeStatus:
            'OPEN',

          occurrenceCount,

          occurredAt:
            input.occurredAt,

          resolvedAt:
            null,

          resolutionReason:
            null,

          safeFailureMessage:
            input.safeFailureMessage,

          classification:
            'TREE_GOVERNOR_INTERNAL_ONLY',

          containsProjectionPayload:
            false,

          containsFamilyMemberData:
            false,

          containsRelationshipData:
            false,

          containsAuthenticationData:
            false,

          containsMembershipData:
            false,

          containsStackTrace:
            false,

          affectsRuntimeDecision:
            false,

          affectsRuntimeGuard:
            false,

          affectsOperationalCertification:
            false,

          certifiesTreeHealth:
            false,

          certifiesReleaseReadiness:
            false,

          certifiesDeploymentReadiness:
            false,

          memberFacing:
            false,
        });

      const activeEpisodes =
        [
          Object.freeze({
            correlationId:
              input.correlationId,

            episodeId,

            eventKind:
              input.eventKind,

            reason:
              input.reason,

            openedAt:
              activeEpisode?.openedAt ??
              input.occurredAt,

            lastOccurrenceAt:
              input.occurredAt,

            occurrenceCount,
          }),

          ...current.activeEpisodes.filter(
            episode =>
              episode.episodeId !==
              episodeId,
          ),
        ];

      updateEnvelope({
        records:
          [
            record,
            ...current.records.filter(
              existing =>
                existing.recordId !==
                record.recordId,
            ),
          ],

        activeEpisodes,
      });

      return record;
    } catch {
      return null;
    }
  };

export const closeCanonicalTreeHistoricalAuthorityPresentationLifecycleEpisodes =
  (
    resolvedAt:
      string =
        new Date().toISOString(),
  ): number => {
    try {
      const current =
        loadEnvelope();

      if (
        current.activeEpisodes.length ===
        0
      ) {
        return 0;
      }

      const activeIds =
        new Set(
          current.activeEpisodes.map(
            episode =>
              episode.episodeId,
          ),
        );

      const closedRecords =
        current.records.map(
          record =>
            activeIds.has(
              record.episodeId,
            ) &&
            record.episodeStatus ===
              'OPEN'
              ? Object.freeze({
                  ...record,

                  episodeStatus:
                    'CLOSED' as const,

                  resolvedAt,

                  resolutionReason:
                    'AVAILABLE_PROJECTION' as const,
                })
              : record,
        );

      const closedCount =
        current.activeEpisodes.length;

      updateEnvelope({
        records:
          closedRecords,

        activeEpisodes:
          [],
      });

      publishLifecycleEvidence(
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_EPISODE_CLOSED',
        {
          closedCount,

          resolvedAt,
        },
      );

      return closedCount;
    } catch {
      return 0;
    }
  };

export const readCanonicalTreeHistoricalAuthorityPresentationLifecycle =
  ():
  CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot => {
    const envelope =
      loadEnvelope();

    return Object.freeze({
      generatedAt:
        new Date().toISOString(),

      schemaVersion:
        2,

      totalRecords:
        envelope.records.length,

      openEpisodeCount:
        envelope.activeEpisodes.length,

      records:
        freezeArray(
          envelope.records,
        ),

      activeEpisodes:
        freezeArray(
          envelope.activeEpisodes,
        ),

      quarantine:
        envelope.quarantine,

      migration:
        envelope.migration,
    });
  };

export const reloadCanonicalTreeHistoricalAuthorityPresentationLifecycleFromStorage =
  (): CanonicalTreeHistoricalAuthorityPresentationLifecycleSnapshot => {
    loadEnvelope(
      true,
    );

    return readCanonicalTreeHistoricalAuthorityPresentationLifecycle();
  };

export const subscribeCanonicalTreeHistoricalAuthorityPresentationLifecycle =
  (
    callback:
      CanonicalTreeHistoricalAuthorityPresentationLifecycleSubscriber,
  ): (() => void) => {
    lifecycleSubscribers.add(
      callback,
    );

    const storageHandler = (
      event:
        StorageEvent,
    ): void => {
      if (
        event.key !==
          STORAGE_KEY
      ) {
        return;
      }

      callback(
        reloadCanonicalTreeHistoricalAuthorityPresentationLifecycleFromStorage(),
      );
    };

    if (
      typeof window !==
        'undefined'
    ) {
      window.addEventListener(
        'storage',
        storageHandler,
      );
    }

    callback(
      readCanonicalTreeHistoricalAuthorityPresentationLifecycle(),
    );

    let active =
      true;

    return (): void => {
      if (!active) {
        return;
      }

      active =
        false;

      lifecycleSubscribers.delete(
        callback,
      );

      if (
        typeof window !==
          'undefined'
      ) {
        window.removeEventListener(
          'storage',
          storageHandler,
        );
      }
    };
  };

export const clearCanonicalTreeHistoricalAuthorityPresentationLifecycleForTest =
  (): void => {
    memoryEnvelope =
      createEmptyEnvelope();

    loaded =
      true;

    if (
      typeof window !==
      'undefined'
    ) {
      try {
        window.localStorage.removeItem(
          STORAGE_KEY,
        );

        window.localStorage.removeItem(
          LEGACY_STORAGE_KEY,
        );
      } catch {
        // Test cleanup remains non-fatal.
      }
    }
  };

export const resolveCanonicalTreeHistoricalAuthorityPresentationLifecycleImplementation =
  (): CanonicalTreeHistoricalAuthorityPresentationLifecycleImplementation =>
    Object.freeze({
      milestone:
        'CC-TREE-ARCH-082',

      authority:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_IMPLEMENTATION',

      decision:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_IMPLEMENTED',

      auditMilestone:
        'CC-TREE-ARCH-081',

      schemaVersion:
        2,

      legacySchemaVersion:
        1,

      boundedHistoryCapacity:
        64,

      deterministicMigrationImplemented:
        true,

      deterministicRecordIdsImplemented:
        true,

      deterministicEpisodeIdsImplemented:
        true,

      episodeScopedOccurrenceCountingImplemented:
        true,

      availableProjectionResolutionImplemented:
        true,

      closedRecordsRetained:
        true,

      crossTabSynchronizationImplemented:
        true,

      crossTabEvidenceRepublishingPrevented:
        true,

      corruptionQuarantineImplemented:
        true,

      rawCorruptPayloadPersistenceAuthorized:
        false,

      internalResetRestricted:
        true,

      manualCommandCenterClearAuthorized:
        false,

      canonicalGovernorRuntimeDispatchInvoked:
        false,

      runtimeDecisionDependencyAdded:
        false,

      runtimeGuardDependencyAdded:
        false,

      operationalCertificationDependencyAdded:
        false,

      certifiesTreeHealth:
        false,

      certifiesReleaseReadiness:
        false,

      certifiesDeploymentReadiness:
        false,

      memberFacingStateAdded:
        false,

      lifecycleCoreImplemented:
        true,

      productionWiringCompleted:
        true,

      nextGovernedDependency:
        'TREE_HISTORICAL_AUTHORITY_PRESENTATION_OBSERVABILITY_LIFECYCLE_CERTIFICATION',
    });
